import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHardDrive, FiUpload, FiFile, FiTrendingUp } = FiIcons;

function StorageStats({ files, refreshTrigger }) {
  const navigate = useNavigate();
  const totalSize = files.reduce((acc, file) => acc + file.size, 0);
  const totalFiles = files.length;
  const storageLimit = 5 * 1024 * 1024 * 1024; // 5GB limit
  const usagePercentage = (totalSize / storageLimit) * 100;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const stats = [
    { label: 'Total Files', value: totalFiles.toString(), icon: FiFile, color: 'blue', change: '+12%' },
    { label: 'Storage Used', value: formatFileSize(totalSize), icon: FiHardDrive, color: 'green', change: `${usagePercentage.toFixed(1)}%` },
    { label: 'This Month', value: totalFiles.toString(), icon: FiUpload, color: 'purple', change: '+5' }
  ];

  const handleUploadClick = () => {
    // Scroll to upload section
    const uploadElement = document.getElementById('file-upload-section');
    if (uploadElement) {
      uploadElement.scrollIntoView({ behavior: 'smooth' });
      // Trigger file upload dialog
      const fileInput = uploadElement.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.click();
      }
    }
  };

  const handleManageStorageClick = () => {
    navigate('/profile');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="space-y-6"
    >
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Storage Overview</h3>
        <div className="space-y-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                  <SafeIcon icon={stat.icon} className={`w-5 h-5 text-${stat.color}-600`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">{stat.change}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Storage Usage</span>
            <span className="text-sm text-gray-900 font-medium">
              {formatFileSize(totalSize)} / {formatFileSize(storageLimit)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(usagePercentage, 100)}%` }}
              transition={{ duration: 1, delay: 0.5 }}
              className={`h-2 rounded-full ${
                usagePercentage > 90 ? 'bg-red-500' : 
                usagePercentage > 70 ? 'bg-yellow-500' : 
                'bg-green-500'
              }`}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {usagePercentage > 90 ? 'Storage almost full' : 
             usagePercentage > 70 ? 'Storage getting full' : 
             'Storage usage is healthy'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="space-y-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleUploadClick}
            className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiUpload} className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">Upload Files</span>
            </div>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleManageStorageClick}
            className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiHardDrive} className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-900">Manage Storage</span>
            </div>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

export default StorageStats;